import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { FieldDropdown } from 'src/components/FieldDropdown'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import { selectSalesOverview } from 'src/features/receivement/ReceivementSelectors'
import { actions } from 'src/features/receivement/ReceivementState'
import {
  ProductStatusEnum, SalesBatchesValue, SalesDropdownOptions, SalesReceivementBatch,
} from 'src/features/receivement/ReceivementTypes'
import { useSelector } from 'src/utils/store'
import * as S from '../styles'

interface Props {
  batch: SalesReceivementBatch
  salesPerDay: number
  productStatus: ProductStatusEnum;
  invoiceNumber: number;
  idReceivementAuthorization: number;
  handleErrorQuantity: (value: number, option?: string) => void;
  productIsRejected?: boolean;
}

export const dropdownOptions = {
  ACCEPT_ALL: 'Aceitar sem avarias',
  ACCEPT_ALL_AND_DAMAGED: 'Aceitar com avarias',
  ACCEPT_EVERYTHING: 'Aceitar tudo',
  REJECT_ALL: 'Rejeitar tudo',
  PARTIAL: 'Parcial',
}

const ActionDropdown = ({
  batch,
  salesPerDay,
  productStatus,
  productIsRejected,
  invoiceNumber,
  idReceivementAuthorization,
  handleErrorQuantity,
}: Props) => {
  const dispatch = useDispatch()
  const batchState = useSelector((state) => state.receivement.batchesStateByBatchItemOrderIds[batch.idsBatchItemOrder.join(',')])
  const isProductNotReceived = productStatus === ProductStatusEnum.DIVERGENT && batch.totalQuantity === 0
  const salesOverview = useSelector(selectSalesOverview)
  const ref = React.useRef(false)
  const [selectedAction, setSelectedAction] = useState<SalesDropdownOptions>()
  const [openDecisionConfirmationModal, setOpenDecisionConfirmationModal] = useState(false)
  const closeConfirmationModal = () => setOpenDecisionConfirmationModal(false)

  const getOptions = () => {
    let optionsArray = Object.values(dropdownOptions)

    if (salesOverview) {
      if (!salesOverview.allowInvoiceReturn || !salesOverview.allowPartialReturn) {
        optionsArray = optionsArray.filter(
          (o) => o !== dropdownOptions.ACCEPT_ALL && o !== dropdownOptions.PARTIAL && o !== dropdownOptions.ACCEPT_ALL_AND_DAMAGED,
        )
      } else {
        optionsArray = optionsArray.filter(
          (o) => o !== dropdownOptions.ACCEPT_EVERYTHING,
        )

        if (batch.damageQuantity === 0) {
          optionsArray = optionsArray.filter(
            (o) => o !== dropdownOptions.ACCEPT_ALL_AND_DAMAGED,
          )
        }

        if (batch.usefulQuantity === 0) {
          optionsArray = optionsArray.filter(
            (o) => o !== dropdownOptions.ACCEPT_ALL,
          )
        }
      }
    }

    return optionsArray
  }

  const handleOption = React.useCallback((event) => {
    const optionResult: Record<string, () => Partial<SalesBatchesValue>> = {
      [dropdownOptions.ACCEPT_ALL]: () => ({
        acceptedQuantity: batch.usefulQuantity,
        returnQuantity: batch.damageQuantity,
        stockDays: salesPerDay !== 0 ? batch.usefulQuantity / salesPerDay : 0,
        option: 'ACCEPT_ALL',
      }),

      [dropdownOptions.ACCEPT_ALL_AND_DAMAGED]: () => ({
        acceptedQuantity: batch.usefulQuantity + batch.damageQuantity,
        returnQuantity: undefined,
        option: 'ACCEPT_ALL_AND_DAMAGED',
        stockDays: salesPerDay !== 0 ? (batch.usefulQuantity + batch.damageQuantity) / salesPerDay : 0,
        acceptDamage: true,
      }),

      [dropdownOptions.ACCEPT_EVERYTHING]: () => ({
        acceptedQuantity: batch.usefulQuantity + batch.damageQuantity,
        returnQuantity: undefined,
        option: 'ACCEPT_EVERYTHING',
        stockDays: salesPerDay !== 0 ? (batch.usefulQuantity + batch.damageQuantity) / salesPerDay : 0,
        acceptDamage: true,
      }),

      [dropdownOptions.REJECT_ALL]: () => ({
        acceptedQuantity: 0,
        returnQuantity: batch.totalQuantity,
        swapQuantity: undefined,
        stockDays: 0,
        option: 'REJECT_ALL',
      }),

      [dropdownOptions.PARTIAL]: () => ({
        acceptedQuantity: 0,
        returnQuantity: undefined,
        option: 'PARTIAL',
        stockDays: 0,
        acceptDamage: true,
      }),
    }

    const newOption = event.target.value as SalesDropdownOptions

    const value = optionResult[newOption]()

    dispatch(actions.updateBatchState({ [batch.idsBatchItemOrder.join(',')]: { ...batchState, ...value } }))
    handleErrorQuantity(value.acceptedQuantity!, newOption)
  }, [
    dispatch,
    batch.idsBatchItemOrder,
    batch.usefulQuantity,
    batch.damageQuantity,
    batch.totalQuantity,
    batchState,
    salesPerDay,
    handleErrorQuantity,
  ])

  const byPassConfirmDecision = () => {
    if (!salesOverview?.products) return false
    const count = salesOverview.products.filter((product) => product.idReceivementAuthorization === idReceivementAuthorization)
    return count.length > 1
  }

  const handleOptionNotAllowInvoiceReturn = React.useCallback((event) => {
    const newOption = event.target.value as SalesDropdownOptions
    if (newOption) {
      setSelectedAction(newOption)
      setOpenDecisionConfirmationModal(true)
    }
  }, [])

  const handleChangeOptionNotAllowInvoiceReturn = React.useCallback(
    (newOption: string) => {
      dispatch(
        actions.setActionForAllProducts({
          idReceivementAuthorization,
          option: newOption,
        }),
      )
      closeConfirmationModal()
    },
    [dispatch, idReceivementAuthorization],
  )

  React.useEffect(() => {
    if (ref.current) return
    if (
      productStatus === ProductStatusEnum.CORRECT
      && !batchState.acceptDamage
      && !batchState.acceptedQuantity
      && (!salesOverview?.allowPartialReturn)
    ) {
      dispatch(actions.setActionForAllProductsInAr({ idReceivementAuthorization }))
    } else if (
      productStatus === ProductStatusEnum.CORRECT
      && !batchState.acceptDamage
      && !batchState.acceptedQuantity
      && batch.usefulQuantity === 0
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: 0,
            returnQuantity: batch.totalQuantity,
            option: 'REJECT_ALL',
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.CORRECT
      && !batchState.acceptDamage
      && !batchState.acceptedQuantity
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: batch.usefulQuantity,
            option: 'ACCEPT_ALL',
            stockDays: salesPerDay !== 0 ? batch.usefulQuantity / salesPerDay : 0,
          },
        }),
      )
    } else if (isProductNotReceived && !batch.selectedOption) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: 0,
            option: 'REJECT_ALL',
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.DIVERGENT
      && batch.damageType === 'VALIDADE'
      && !batchState.acceptDamage
      && !batchState.acceptedQuantity
      && batch.usefulQuantity > 0
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            acceptedQuantity: batch.usefulQuantity,
            option: 'PARTIAL',
          },
        }),
      )
    } else if (
      productStatus === ProductStatusEnum.DIVERGENT
      && !batchState.acceptDamage
      && !batchState.acceptedQuantity
      && batch.usefulQuantity === 0
    ) {
      dispatch(
        actions.updateBatchState({
          [batch.idsBatchItemOrder.join(',')]: {
            ...batchState,
            returnQuantity: batch.totalQuantity,
            acceptedQuantity: 0,
            option: 'REJECT_ALL',
          },
        }),
      )
    }

    ref.current = true
  }, [
    batch.acceptedQuantity,
    batch.usefulQuantity,
    batch.selectedOption,
    batch.damageType,
    batch.totalQuantity,
    batch.expirationDate,
    productStatus,
    salesPerDay,
    isProductNotReceived,
    batchState,
    batch.idsBatchItemOrder,
    dispatch,
    salesOverview?.allowInvoiceReturn,
    salesOverview?.allowPartialReturn,
    idReceivementAuthorization,
  ])

  const handleSupplierMessage = () => {
    let conditionalMessage = 'Esse fornecedor não aceita notas de devolução'
    if (salesOverview?.allowInvoiceReturn && !salesOverview?.allowPartialReturn) conditionalMessage = `${conditionalMessage} parcial`

    return (
      <>
        <p>
          {conditionalMessage}, a ação <strong>{`${selectedAction?.toUpperCase()}`}</strong> será espelhada
          para todos os itens da nota <strong>{`${invoiceNumber}`}</strong>.
        </p>
        <p>
          Deseja prosseguir?
        </p>
      </>
    )
  }

  return (
    <>
      <FieldDropdown
        data-testid='type-input'
        value={
          !batch.selectedOption
            ? (batchState.option ? dropdownOptions[batchState.option] : '')
            : batch.selectedOption
        }
        disabled={isProductNotReceived || productIsRejected}
        options={getOptions()}
        onChange={
          !salesOverview?.allowInvoiceReturn && byPassConfirmDecision()
            ? handleOptionNotAllowInvoiceReturn
            : salesOverview?.allowInvoiceReturn && !salesOverview?.allowPartialReturn && byPassConfirmDecision()
              ? handleOptionNotAllowInvoiceReturn
              : handleOption
        }
        containerStyle={{ padding: 0 }}
        style={{
          border: 0,
          height: 22,
          backgroundColor: 'transparent',
          padding: 0,
          boxShadow: 'none',
          fontFamily: 'Work Sans',
          color: 'inherit',
          fontSize: 14,
        }}
      />

      {
        selectedAction
        && (
        <ConfirmOrRejectModal
          show={openDecisionConfirmationModal}
          title='Confirmação de decisão'
          customizableDescription={(
            <S.ContainerDescription>
              {handleSupplierMessage()}
            </S.ContainerDescription>
          )}
          onConfirm={() => handleChangeOptionNotAllowInvoiceReturn(selectedAction)}
          onReject={closeConfirmationModal}
          onClose={closeConfirmationModal}
          dataTestId='modal-confirmation-spread-option'
        />
        )

      }
    </>
  )
}

export { ActionDropdown }
